import React from 'react';
import {
    Input
} from 'shared/components/ui/input';
import {
    Label
} from 'shared/components/ui/label';
import {
    Button
} from 'shared/components/ui/button';
import {
    Switch
} from 'shared/components/ui/switch';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
 Plus, Trash2
} from 'lucide-react';
import {
    EntitySchema, EntitySchemaProperty, EntitySchemaPropertyType
} from '../types/builderAssistant.types';

const PROPERTY_TYPES: EntitySchemaPropertyType[] = [
    'string', 'number', 'integer', 'boolean', 'array', 'object',
];

interface EntitySchemaBuilderProps {
  value: EntitySchema;
  onChange: (next: EntitySchema) => void;
}

// Minimal JSON-Schema builder constrained to `type: object`. Lets the builder
// declare field name + type + optional pattern + required flag. The final
// schema is what Mind's `validate_entity` enforces per execute request.
export default function EntitySchemaBuilder({
 value, onChange
}: EntitySchemaBuilderProps) {
    const properties = value.properties || {};
    const required = value.required || [];

    const updateProp = (name: string, patch: Partial<EntitySchemaProperty>) => {
        const prev = properties[name] || {
 type: 'string'
};
        onChange({
            ...value,
            properties: {
 ...properties,
[name]: {
 ...prev,
...patch
}
},
        });
    };

    const renameProp = (oldName: string, newName: string) => {
        if (!newName || newName === oldName) return;
        if (properties[newName]) return;
        const nextProps: Record<string, EntitySchemaProperty> = {};
        for (const [k, v] of Object.entries(properties)) {
            nextProps[k === oldName ? newName : k] = v;
        }
        const nextRequired = required.map(r => (r === oldName ? newName : r));
        onChange({
 ...value,
properties: nextProps,
required: nextRequired
});
    };

    const removeProp = (name: string) => {
        const nextProps = {
 ...properties
};
        delete nextProps[name];
        const nextRequired = required.filter(r => r !== name);
        onChange({
 ...value,
properties: nextProps,
required: nextRequired
});
    };

    const toggleRequired = (name: string, req: boolean) => {
        const nextRequired = req
            ? Array.from(new Set([...required, name]))
            : required.filter(r => r !== name);
        onChange({
 ...value,
required: nextRequired
});
    };

    const addProp = () => {
        const base = 'field';
        let i = 1;
        while (properties[`${base}_${i}`]) i++;
        const name = `${base}_${i}`;
        onChange({
            ...value,
            properties: {
 ...properties,
[name]: {
 type: 'string'
}
},
        });
    };

    const entries = Object.entries(properties);

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <Label>Entity Schema Fields</Label>
                <div className="flex items-center gap-2">
                    <Label className="text-xs">Allow unknown keys</Label>
                    <Switch
                        checked={value.additionalProperties}
                        onCheckedChange={(v) => onChange({
 ...value,
additionalProperties: !!v
})}
                    />
                </div>
            </div>

            {entries.length === 0 && (
                <div className="text-xs text-muted-foreground italic">
                    No fields declared — entity validation is skipped at runtime.
                </div>
            )}

            <div className="space-y-2">
                {entries.map(([name, prop]) => {
                    const isRequired = required.includes(name);
                    return (
                        <div
                            key={name}
                            className="grid grid-cols-[1.5fr_1fr_1.5fr_auto_auto] gap-2 items-center border rounded-md p-2"
                        >
                            <Input
                                value={name}
                                onChange={(e) => renameProp(name, e.target.value)}
                                placeholder="field_name"
                                className="font-mono text-xs"
                            />
                            <Select
                                value={prop.type}
                                onValueChange={(v) => updateProp(name, {
 type: v as EntitySchemaPropertyType
})}
                            >
                                <SelectTrigger className="text-xs">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {PROPERTY_TYPES.map(t => (
                                        <SelectItem key={t} value={t}>{t}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Input
                                value={prop.pattern as string || ''}
                                onChange={(e) => updateProp(name, {
 pattern: e.target.value || undefined
})}
                                placeholder="regex pattern (optional)"
                                className="font-mono text-xs"
                                disabled={prop.type !== 'string'}
                            />
                            <div className="flex items-center gap-1">
                                <Switch
                                    checked={isRequired}
                                    onCheckedChange={(v) => toggleRequired(name, !!v)}
                                />
                                <span className="text-xs">req</span>
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeProp(name)}
                                title="Remove field"
                            >
                                <Trash2 className="h-3 w-3" />
                            </Button>
                        </div>
                    );
                })}
            </div>

            <Button variant="outline" size="sm" onClick={addProp}>
                <Plus className="mr-1 h-3 w-3" /> Add field
            </Button>
        </div>
    );
}
